/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { ALL_LANGUAGES, URL_LANGUAGES } from "@/lib/constants";

/**
 * Matches a leading language prefix, and only a whole path segment of one.
 *
 * The lookahead is the part that matters: without it "/esports" strips to
 * "ports". No route on this site begins with a two-letter language code
 * followed by more letters today, so nothing is mangled in practice — but
 * this helper now decides which page Deskshare Support appears on, and
 * a mangled path there shows or hides the widget on the wrong page.
 *
 * ALL_LANGUAGES, not URL_LANGUAGES. The two lists answer different questions
 * and both are needed in this file: URL_LANGUAGES is the five locales that
 * get a prefix when the site BUILDS a link; ALL_LANGUAGES is the six the site
 * must RECOGNISE when reading one. A legacy /en/ address never resolves to a
 * registered route, but it can still arrive in a ?redirect= value from an old
 * email or a hand-edited link, and safeRedirect.ts has to recognise it.
 */
const LANG_PREFIX = new RegExp(`^/(${ALL_LANGUAGES.join("|")})(?=/|$)`);

/**
 * Removes a leading /en, /de, /es, /fr, /it or /pt from a pathname.
 *
 * The single shared copy of a rule that previously existed three times, in
 * three slightly different forms (V-8 items 2 and 4). Returns "/" for a bare
 * prefix so callers never have to handle an empty string.
 *
 * @example
 *   stripLanguagePrefix("/de/product-support")  // → "/product-support"
 *   stripLanguagePrefix("/en/auth")             // → "/auth"
 *   stripLanguagePrefix("/esports")             // → "/esports"
 *   stripLanguagePrefix("/de")                  // → "/"
 */
export function stripLanguagePrefix(pathname: string): string {
  return pathname.replace(LANG_PREFIX, "") || "/";
}

/**
 * Splits a pathname into its language prefix and the remaining path.
 *
 * @example
 *   normalizeLocalizedPath('/de/screen-recorder')
 *   // → { lang: 'de', rest: 'screen-recorder' }
 *
 *   normalizeLocalizedPath('/screen-recorder')
 *   // → { lang: null, rest: 'screen-recorder' }
 */
export function normalizeLocalizedPath(pathname: string): {
  lang: string | null;
  rest: string;
} {
  const parts = pathname.split("/").filter(Boolean);
  const first = parts[0];
  const isLang = Boolean(
    first && (URL_LANGUAGES as readonly string[]).includes(first),
  );
  return {
    lang: isLang ? first : null,
    rest: (isLang ? parts.slice(1) : parts).join("/"),
  };
}

/**
 * The query parameter a link uses to state the language it was built for.
 *
 * Item 5 (Session 361). The set-password link in a purchase email carries no
 * language prefix, so the site fell back to whatever preference was stored on
 * that browser from an earlier, unrelated visit — a customer who had once read
 * a Deskshare page in French was shown the set-password page in French after
 * an entirely English purchase, at the one moment they cannot read their way
 * out of it.
 */
export const LANGUAGE_QUERY_PARAM = "lang";

/**
 * Reads an explicit language out of the current address.
 *
 * An explicit `?lang=` is the site telling itself which language to use — it is
 * put there by the link that sent the visitor here, so it outranks a preference
 * stored on this browser from an earlier visit. A path prefix still wins over
 * both: it is the part of the address the visitor can see.
 *
 * It lives here, beside the path rule, because two places have to apply the
 * same order: the startup resolution in i18n.ts and the language hook the
 * header uses. If those two disagree, the first render honours the link and the
 * next one puts the stored preference back — the page changes language under
 * the customer a moment after it opens. This module has no side effects and no
 * dependency on the translation library, which is what lets both import it.
 *
 * ALL_LANGUAGES, not URL_LANGUAGES: "en" is a language a link may legitimately
 * state, and it must beat a stored "fr".
 */
export function getLanguageFromQuery(
  search: string = typeof window === "undefined" ? "" : window.location.search,
): string | null {
  try {
    const raw = new URLSearchParams(search).get(LANGUAGE_QUERY_PARAM);
    const lang = (raw ?? "").slice(0, 2).toLowerCase();
    return (ALL_LANGUAGES as readonly string[]).includes(lang) ? lang : null;
  } catch {
    /* c8 ignore next 2 */
    // An unparseable address states no language, same as an absent parameter.
    return null;
  }
}

/**
 * The first language this browser asks for that the site actually serves.
 *
 * VS-1 part 1 (Session 376). The report proposed ranking this ABOVE the stored
 * preference; that must not be built. The stored preference is not a stale
 * artefact — it is what the site writes when a customer picks a language from
 * the header — so putting the browser above it means a German-speaking customer
 * on an English-language browser picks German in the header and is put straight
 * back into English on the next page they open, because the language is
 * re-resolved on every navigation. Every visitor whose browser language differs
 * from their chosen language would meet that, on every page change.
 *
 * Below the stored preference it is a real improvement, and it is a better
 * fallback than the geolocation lookup that runs today for a visitor with no
 * preference on record: an IP address tells you where somebody is,
 * `navigator.languages` tells you what they read, it needs no network request,
 * and it cannot be wrong about a German speaker in France.
 *
 * `navigator.languages` is preferred over `navigator.language` because it is
 * the ordered list the visitor actually configured; a regional tag such as
 * `pt-BR` is reduced to its base language, which is the only form the site
 * serves. ALL_LANGUAGES, not URL_LANGUAGES: a browser asking for English is
 * stating a language, not stating nothing.
 */
export function getLanguageFromBrowser(): string | null {
  if (typeof navigator === "undefined") return null;
  const asked =
    navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
  for (const tag of asked) {
    const lang = (tag ?? "").slice(0, 2).toLowerCase();
    if ((ALL_LANGUAGES as readonly string[]).includes(lang)) return lang;
  }
  return null;
}

/**
 * Rebuilds a pathname with the given language prefix applied.
 * English ('en') produces no prefix — all other supported locales
 * get a /{lang}/ prefix prepended.
 *
 * @example
 *   switchLanguagePath('/de/screen-recorder', 'fr')
 *   // → '/fr/screen-recorder'
 *
 *   switchLanguagePath('/screen-recorder', 'de')
 *   // → '/de/screen-recorder'
 *
 *   switchLanguagePath('/de/screen-recorder', 'en')
 *   // → '/screen-recorder'
 */
export function switchLanguagePath(pathname: string, lang: string): string {
  const { rest } = normalizeLocalizedPath(pathname);
  return lang === "en" ? `/${rest}` : `/${lang}/${rest}`;
}

/**
 * True when `pathname` is a page that renders its own minimal chrome instead
 * of the marketing navigation bar and footer, in any language.
 *
 * AL-3 / R5 (Session 447). Two callers and one list: main.tsx nests these
 * routes under StandaloneLayout, and App.tsx reads this to stop rendering the
 * Header and Footer above them. Written as a shared rule rather than a second
 * copy of the same comparison, because this codebase has already been bitten
 * three times by private copies of a path rule drifting apart.
 *
 * The /auth pages are deliberately NOT in here: App.tsx keeps its own
 * `isAuthPage` check, which is a prefix match over seven sub-routes rather
 * than an exact path, and folding the two together would widen one of them.
 */
export function isStandalonePath(pathname: string): boolean {
  const stripped = stripLanguagePrefix(pathname);
  return stripped === "/mypcinfo" || stripped === "/mypcinfo/";
}
